/**
 * MascotBattleModal - Cinematic mascot vs mascot battle.
 *
 * Dramatic entrances, fight animations, play-by-play narrative,
 * and a hype winner reveal. Pure entertainment — mascot vs mascot,
 * who would ACTUALLY win in a fight?
 */

import { useState, useEffect, useRef } from 'react';
import { getMascotBattle } from '../../services/bracketService';

const FIGHT_SOUNDS = [
  'POW!', 'WHAM!', 'CRACK!', 'BOOM!', 'SLASH!',
  'CRUNCH!', 'SMACK!', 'THWACK!', 'KAPOW!', 'WHOOSH!',
];

function MascotBattleModal({ team1EntryId, team2EntryId, onClose, onPickWinner }) {
  const [battle, setBattle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('loading');
  // loading → entrance1 → entrance2 → staredown → fight → knockout → result
  const [fightText, setFightText] = useState('');
  const fightInterval = useRef(null);

  useEffect(() => {
    const fetchBattle = async () => {
      try {
        setLoading(true);
        const data = await getMascotBattle(team1EntryId, team2EntryId);
        setBattle(data);
        // Cinematic sequence
        setPhase('entrance1');
        setTimeout(() => setPhase('entrance2'), 1200);
        setTimeout(() => setPhase('staredown'), 2400);
        setTimeout(() => setPhase('fight'), 3600);
        setTimeout(() => setPhase('knockout'), 5600);
        setTimeout(() => setPhase('result'), 6800);
      } catch (err) {
        setError(err.response?.data?.detail || 'Battle arena malfunction!');
      } finally {
        setLoading(false);
      }
    };
    fetchBattle();
  }, [team1EntryId, team2EntryId]);

  // Cycle fight sound effects during the fight phase
  useEffect(() => {
    if (phase === 'fight') {
      const cycle = () => {
        setFightText(FIGHT_SOUNDS[Math.floor(Math.random() * FIGHT_SOUNDS.length)]);
      };
      cycle();
      fightInterval.current = setInterval(cycle, 400);
      return () => clearInterval(fightInterval.current);
    } else {
      setFightText('');
      if (fightInterval.current) clearInterval(fightInterval.current);
    }
  }, [phase]);

  const isFighting = phase === 'fight';
  const isKnockout = phase === 'knockout';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`bg-surface border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden transition-transform duration-100 ${
          isFighting ? 'animate-shake' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-900/40 via-purple-900/50 to-blue-900/40 px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">&#x2694;&#xFE0F;</span>
              <h2 className="text-sm font-bold text-text-primary tracking-wide uppercase">Mascot Battle Arena</h2>
              {isFighting && (
                <span className="text-[10px] font-bold text-red-400 uppercase animate-pulse tracking-widest">LIVE</span>
              )}
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary transition-colors text-lg leading-none">&times;</button>
          </div>
        </div>

        <div className="p-4 space-y-4 min-h-[280px]">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="text-4xl animate-bounce">&#x2694;&#xFE0F;</div>
              <p className="text-sm text-text-secondary animate-pulse">Setting up the arena...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center py-12">
              <p className="text-sm text-red-400">{error}</p>
              <button onClick={onClose} className="mt-3 text-xs text-text-tertiary hover:text-text-primary">Close</button>
            </div>
          )}

          {battle && !loading && (
            <>
              {/* Entrance Phase 1 — Team 1 slides in */}
              {phase === 'entrance1' && (
                <div className="flex flex-col items-center py-8 animate-slideInLeft">
                  <div className="text-[10px] text-red-400 uppercase tracking-[0.3em] font-bold mb-3">Entering the Arena...</div>
                  {battle.team1.logo_url && (
                    <img src={battle.team1.logo_url} alt={battle.team1.mascot} className="w-20 h-20 mb-3" />
                  )}
                  <div className="text-lg font-black text-text-primary">{battle.team1.mascot}</div>
                  <div className="text-xs text-text-tertiary">#{battle.team1.seed} {battle.team1.team_name}</div>
                </div>
              )}

              {/* Entrance Phase 2 — Team 2 slides in */}
              {phase === 'entrance2' && (
                <div className="flex flex-col items-center py-8 animate-slideInRight">
                  <div className="text-[10px] text-blue-400 uppercase tracking-[0.3em] font-bold mb-3">The Challenger...</div>
                  {battle.team2.logo_url && (
                    <img src={battle.team2.logo_url} alt={battle.team2.mascot} className="w-20 h-20 mb-3" />
                  )}
                  <div className="text-lg font-black text-text-primary">{battle.team2.mascot}</div>
                  <div className="text-xs text-text-tertiary">#{battle.team2.seed} {battle.team2.team_name}</div>
                </div>
              )}

              {/* Staredown */}
              {phase === 'staredown' && (
                <div className="py-6 animate-fadeIn">
                  <div className="flex items-center justify-between px-4">
                    <div className="text-center">
                      {battle.team1.logo_url && (
                        <img src={battle.team1.logo_url} alt={battle.team1.mascot} className="w-16 h-16 mx-auto mb-1" />
                      )}
                      <div className="text-xs font-bold text-text-primary">{battle.team1.mascot}</div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-2xl font-black text-red-500 animate-pulse">VS</div>
                      <div className="text-[9px] text-text-tertiary uppercase tracking-widest">Fight!</div>
                    </div>
                    <div className="text-center">
                      {battle.team2.logo_url && (
                        <img src={battle.team2.logo_url} alt={battle.team2.mascot} className="w-16 h-16 mx-auto mb-1" />
                      )}
                      <div className="text-xs font-bold text-text-primary">{battle.team2.mascot}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* FIGHT! */}
              {isFighting && (
                <div className="py-4">
                  <div className="flex items-center justify-between px-4">
                    <div className="text-center">
                      {battle.team1.logo_url && (
                        <img
                          src={battle.team1.logo_url}
                          alt={battle.team1.mascot}
                          className="w-14 h-14 mx-auto animate-attackLeft"
                        />
                      )}
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="text-2xl font-black text-yellow-400 animate-bounce">
                        {fightText}
                      </div>
                      <div className="text-3xl">&#x1F4A5;</div>
                    </div>
                    <div className="text-center">
                      {battle.team2.logo_url && (
                        <img
                          src={battle.team2.logo_url}
                          alt={battle.team2.mascot}
                          className="w-14 h-14 mx-auto animate-attackRight"
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <div className="text-[10px] text-red-400 font-bold uppercase tracking-widest animate-pulse">
                      &#x1F534; Battle in Progress
                    </div>
                  </div>
                </div>
              )}

              {/* Knockout */}
              {isKnockout && (
                <div className="py-6 animate-fadeIn">
                  <div className="flex items-center justify-between px-4">
                    <div className={`text-center transition-all duration-500 ${battle.winner === 'team1' ? 'scale-110' : 'opacity-30 scale-75'}`}>
                      {battle.team1.logo_url && (
                        <img src={battle.team1.logo_url} alt={battle.team1.mascot}
                          className={`w-16 h-16 mx-auto mb-1 ${battle.winner === 'team1' ? '' : 'grayscale rotate-12'}`}
                        />
                      )}
                      <div className="text-xs font-bold text-text-primary">{battle.team1.mascot}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl mb-1">&#x1F4A2;</div>
                      <div className="text-xs font-black text-yellow-400 uppercase tracking-wider">K.O.!</div>
                    </div>
                    <div className={`text-center transition-all duration-500 ${battle.winner === 'team2' ? 'scale-110' : 'opacity-30 scale-75'}`}>
                      {battle.team2.logo_url && (
                        <img src={battle.team2.logo_url} alt={battle.team2.mascot}
                          className={`w-16 h-16 mx-auto mb-1 ${battle.winner === 'team2' ? '' : 'grayscale -rotate-12'}`}
                        />
                      )}
                      <div className="text-xs font-bold text-text-primary">{battle.team2.mascot}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Result */}
              {phase === 'result' && (
                <div className="animate-fadeIn space-y-3">
                  {/* Winner banner */}
                  <div className="bg-gradient-to-r from-yellow-900/30 via-yellow-800/40 to-yellow-900/30 border border-yellow-600/40 rounded-lg p-4 text-center">
                    <div className="text-[10px] text-yellow-500/80 uppercase tracking-[0.2em] mb-2">&#x1F3C6; Champion &#x1F3C6;</div>
                    {(battle.winner === 'team1' ? battle.team1 : battle.team2).logo_url && (
                      <img
                        src={(battle.winner === 'team1' ? battle.team1 : battle.team2).logo_url}
                        alt="winner"
                        className="w-14 h-14 mx-auto mb-2 ring-2 ring-yellow-400 rounded-full"
                      />
                    )}
                    <div className="text-lg font-black text-yellow-300">
                      {battle.winner === 'team1' ? battle.team1.mascot : battle.team2.mascot}
                    </div>
                    <div className="text-xs text-text-tertiary mt-0.5">
                      {battle.winner === 'team1' ? battle.team1.team_name : battle.team2.team_name}
                    </div>
                  </div>

                  {/* Finishing move */}
                  <div className="text-center">
                    <div className="text-[10px] text-text-tertiary uppercase tracking-wider">Finishing Move</div>
                    <div className="text-sm font-bold text-red-400 mt-0.5">{battle.winner_move}</div>
                  </div>

                  {/* Battle narrative */}
                  <div className="bg-black/20 border border-border/50 rounded-lg p-4">
                    <div className="text-[9px] text-text-tertiary uppercase tracking-wider mb-2">Play-by-Play</div>
                    <p className="text-sm text-text-secondary italic leading-relaxed">
                      &quot;{battle.battle_narrative}&quot;
                    </p>
                  </div>

                  {/* Crowd reaction */}
                  <p className="text-xs text-text-tertiary text-center italic">
                    {battle.crowd_reaction}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {phase === 'result' && (
          <div className="border-t border-border px-4 py-3 space-y-3">
            {onPickWinner && battle && (
              <button
                onClick={() => {
                  const winnerEntryId = battle.winner === 'team1' ? battle.team1.entry_id : battle.team2.entry_id;
                  onPickWinner(winnerEntryId);
                  onClose();
                }}
                className="w-full py-2.5 text-sm font-bold rounded-lg bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-white shadow-lg shadow-yellow-600/20 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pick {battle.winner === 'team1' ? battle.team1.mascot : battle.team2.mascot} as Winner
              </button>
            )}
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-text-tertiary">For entertainment only</span>
              <button
                onClick={onClose}
                className="text-xs text-text-tertiary hover:text-text-primary transition-colors"
              >
                Close Arena
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MascotBattleModal;
